import { Component, type ReactNode } from "react";
import { ROUTES } from "@oshimap/config";
import { navigate } from "../app/router";
import { Button, EmptyState } from "./ui";

/**
 * 화면 하나가 터져도 앱 전체가 사라지지 않게 합니다.
 *
 * ## 왜 필요했나
 *
 * 라우트를 나눠 놓으면 그 조각을 못 받아 오는 순간이 생깁니다 — 배포 직후
 * 옛 주소가 404 가 되거나, 지하철에서 연결이 끊기거나. React 는 `lazy` 안에서
 * 난 오류를 잡아 주지 않습니다. 실제로 확인했습니다: 조각 하나를 치우고
 * 그 주소를 열면 **body 가 통째로 비었습니다.** 하단 메뉴도 없어서 돌아갈
 * 길조차 없었습니다.
 *
 * ## 두 가지를 갈라 말합니다
 *
 * 조각을 못 받은 것과 화면 코드가 터진 것은 사람이 할 일이 다릅니다. 앞은
 * 새로고침하면 대개 낫고(새 버전을 받습니다), 뒤는 새로고침해도 같습니다 —
 * 그때 "다시 시도" 를 권하면 사람을 헛되이 붙잡습니다.
 *
 * 라우트가 바뀌면 다시 시도해야 하므로, 쓰는 쪽에서 `key` 로 갈아 끼웁니다.
 */
const CHUNK_HINTS = [
  "dynamically imported module",
  "Importing a module script failed",
  "Failed to fetch",
  "error loading",
];

function isChunkFailure(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error ?? "");
  return CHUNK_HINTS.some((hint) => message.toLowerCase().includes(hint.toLowerCase()));
}

interface Props { children: ReactNode }
interface State { error: unknown }

export class ScreenBoundary extends Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: unknown): State {
    return { error };
  }

  override render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;

    if (isChunkFailure(error)) {
      /*
       * 오프라인이면 새로고침도 안 됩니다. 되지 않을 것을 권하면 사람은 한 번
       * 더 실패한 뒤에야 상황을 압니다 — 그때는 앱을 탓하게 됩니다.
       */
      const offline = typeof navigator !== "undefined" && navigator.onLine === false;
      return (
        <div className="om-screen om-screen--pad">
          <h1 className="sr-only">화면을 불러오지 못함</h1>
          <EmptyState
            level={2}
            title={offline ? "지금은 연결이 없어요" : "화면을 받아오지 못했어요"}
            body={offline
              ? "이 화면은 아직 이 기기에 받아 두지 않았어요. 연결되면 열립니다."
              : "새 버전이 올라갔거나 연결이 끊겼을 수 있어요. 새로고침하면 대개 해결됩니다."}
            action={offline
              ? <Button variant="secondary" size="sm" onClick={() => navigate(ROUTES.home)}>홈으로</Button>
              : <Button variant="primary" size="sm" onClick={() => window.location.reload()}>새로고침</Button>}
          />
        </div>
      );
    }

    return (
      <div className="om-screen om-screen--pad">
        <h1 className="sr-only">화면 오류</h1>
        <EmptyState
          level={2}
          title="이 화면에서 문제가 생겼어요"
          body="다른 화면은 그대로 쓸 수 있어요. 같은 일이 반복되면 알려주세요."
          action={
            <Button variant="primary" size="sm" onClick={() => navigate(ROUTES.home)}>
              홈으로
            </Button>
          }
        />
      </div>
    );
  }
}
